import { jwtDecode } from 'jwt-decode';
import { apiService } from '../../../services/apiService';
import { trackingService } from '../../../services/TrackingService';

interface User {
  id: string;
  email?: string;
  phone?: string;
  name: string;
  role: string;
  status: string;
}

interface AuthResult {
  success: boolean;
  user: User;
  accessToken: string;
}

interface MessageResponse {
  success: boolean;
  message: string;
}

class AuthService {
  private trackAuthEvent(action: string, eventType: string, details: any, userId?: string) {
    try {
      trackingService.recordEvent(
        action,
        'click',
        eventType,
        'Login',
        { ...details, method: details.method || 'password' },
        '1'
      ).then(eventData => {
        eventData.userId = userId || '';
        eventData.studentId = userId || '';
        eventData.page = { pageId: 'login_page', url: '/login' };
      });
    } catch (error) {
      console.warn('Failed to track auth event:', error);
    }
  }

  async loginWithEmail(email: string, password: string): Promise<AuthResult> {
    try {
      const response = await apiService.post('auth/login', { email, password });
      
      if (!response || !response.token || !response.user) {
        throw new Error('Invalid response from server');
      }

      const { token, user } = response;
      const tokenDecode = jwtDecode(token) as any;

      if (!user.role) {
        throw new Error('User role not found');
      }

      localStorage.setItem('accessToken', token);
      localStorage.setItem('studentId', user.id);
      localStorage.setItem('jwtDecode', JSON.stringify(tokenDecode));

      // Trigger Moodle SSO session in the background (fire-and-forget)
      // This sets an HTTP-only cookie so Moodle's OAuth2 login can auto-complete.
      this.triggerMoodleSsoSession(token);

      this.trackAuthEvent('login_submit', 'LoginSuccess', {
        usernameEntered: !!email,
        passwordEntered: !!password,
        success: true,
        error: ''
      }, user.id);
      
      return {
        success: true,
        user: {
          id: user.id,
          email: user.email,
          phone: user.phone,
          name: user.name,
          role: user.role,
          status: 'active'
        },
        accessToken: token
      };
    } catch (error: any) {
      this.trackAuthEvent('login_submit', 'LoginFailed', {
        usernameEntered: !!email,
        passwordEntered: !!password,
        success: false,
        error: error.message || 'Something went wrong'
      }, email);
      throw error;
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<MessageResponse> {
    try {
      const response = await apiService.put('/auth/change-password', {
        currentPassword:oldPassword,
        newPassword
      });
      return { 
        success: true, 
        message: response.message || 'Password changed successfully' 
      };
    } catch (error: any) {
      throw error;
    }
  }


  async signOut(): Promise<void> {
    try {
      const userId = localStorage.getItem('studentId');
      if (userId) {
        await trackingService.recordEvent(
          'logout_submit',
          'click',
          'LogoutSuccess',
          'LogoutConfirmation',
          { method: 'logout', success: true, error: '' },
          '1'
        ).then(eventData => {
          eventData.userId = userId;
          eventData.studentId = userId;
        });
      }
    } catch (error) {
      console.error('Sign out tracking error:', error);
    }

    const preserveKeys = ['sidebarCollapsed', 'navigationMode', 'themeMode', 'app_feedback_submitted'];
    Object.keys(localStorage).forEach(key => {
      if (!preserveKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    sessionStorage.clear();
    apiService.logout();

    // Clear Moodle SSO session cookie on sign-out
    this.clearMoodleSsoSession();
  }

  /**
   * Calls the NestJS OIDC /session endpoint to set an HTTP-only SSO cookie.
   * This allows Moodle's auth/oauth2 flow to auto-complete without a second login prompt.
   * Fire-and-forget; errors are silently swallowed so they never break the Falco login flow.
   */
  private triggerMoodleSsoSession(token: string): void {
    fetch('/api/oidc/session', {
      method: 'POST',
      credentials: 'include', // needed to receive and store the Set-Cookie header
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).catch(() => {
      // Non-critical – Moodle SSO is best-effort
    });
  }

  private clearMoodleSsoSession(): void {
    fetch('/api/oidc/session/clear', {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  }
}

export const authService = new AuthService();
